<template>
  <div class="flex h-screen bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
    <!-- Sidebar táctil -->
    <aside
      :class="[
        'bg-gray-900 text-white flex flex-col py-6 transition-all duration-300 ease-in-out shadow-xl z-20',
        colapsado ? 'w-20 px-3' : 'w-72 px-6',
      ]"
      class="relative"
    >
      <!-- Botón colapsar -->
      <button
        @click="colapsado = !colapsado"
        class="absolute top-4 right-4 text-white hover:text-blue-400 transition"
        :title="colapsado ? 'Expandir menú' : 'Colapsar menú'"
      >
        <component :is="colapsado ? Menu : ArrowLeft" class="w-6 h-6" />
      </button>

      <!-- Logo o título -->
      <div v-if="!colapsado" class="text-xl font-bold mb-8 pl-2 tracking-wide select-none">
        <span class="text-blue-400">POS</span> Sistema
      </div>

      <!-- Menú lateral -->
      <nav class="flex flex-col space-y-2 mt-12 md:mt-16">
        <button
          v-for="item in menu"
          :key="item.key"
          @click="seccion = item.key"
          :class="[
            'flex items-center py-3 px-4 rounded-xl transition-all duration-200 group',
            seccion === item.key
              ? 'bg-blue-600 shadow-lg'
              : 'hover:bg-gray-800 hover:shadow-inner',
          ]"
          :title="colapsado ? item.label : ''"
        >
          <!-- Ícono -->
          <component :is="item.icon" class="w-6 h-6 text-white shrink-0" />

          <!-- Etiqueta -->
          <transition name="fade">
            <span v-if="!colapsado" class="ml-4 text-sm truncate group-hover:text-blue-200">
              {{ item.label }}
            </span>
          </transition>
        </button>
      </nav>

      <!-- Footer / branding opcional -->
      <div v-if="!colapsado" class="mt-auto text-xs text-gray-400 px-2 pt-6 border-t border-gray-800">
        © {{ new Date().getFullYear() }} TuEmpresa
      </div>
    </aside>

    <!-- Panel principal -->
    <main
      class="flex-1 bg-white rounded-tl-3xl p-6 md:p-8 overflow-y-auto shadow-inner transition-all duration-300"
    >
      <transition name="fade" mode="out-in">
        <component :is="componenteActual" :key="seccion" />
      </transition>
    </main>
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>

<script setup>
import { ref, computed } from "vue";

// Importar componentes POS desde /components/pos/
import POSVenta from "@/components/pos/POSVenta.vue";
import CrearProducto from "@/components/pos/CrearProducto.vue";
import CrearCategoria from "@/components/pos/CrearCategoria.vue";
import CrearTax from "@/components/pos/CrearTax.vue";
import ReporteVentas from "@/components/pos/ReporteVentas.vue";
import ReporteInventario from "@/components/pos/ReporteInventario.vue";
import ReporteTopProductos from "@/components/pos/ReporteTopProductos.vue";
import ReporteTopServicios from "@/components/pos/ReporteTopServicios.vue";
import ReporteZ from "@/components/pos/ReporteZ.vue";
import AdminProductos from "@/components/pos/AdminProductos.vue";
import CuponesDescuento from "@/components/pos/CuponesDescuento.vue";
import {
  ReceiptText,
  ShoppingCart,
  Wrench,
  Folder,
  DollarSign,
  BarChart2,
  Package,
  Flame,
  Leaf,
  Calculator,
  Menu,
  ArrowLeft,
  Percent,
} from "lucide-vue-next";
const colapsado = ref(true);

const userRole = localStorage.getItem("role") || "guest";

const allMenu = [
  { key: "venta", icon: ReceiptText, label: "Punto de Venta" },
  { key: "crear-producto", icon: ShoppingCart, label: "Crear Producto" },
  { key: "admin-productos", icon: Wrench, label: "Administrar Productos" },
  { key: "crear-categoria", icon: Folder, label: "Crear Categoría" },
  { key: "crear-tax", icon: DollarSign, label: "Crear Tax" },
  { key: "cupones-descuento", icon: Percent, label: "Cupones" },
  { key: "reporte-ventas", icon: BarChart2, label: "Reporte Ventas" },
  { key: "reporte-inventario", icon: Package, label: "Inventario" },
  { key: "top-productos", icon: Flame, label: "Top Productos" },
  { key: "top-servicios", icon: Leaf, label: "Top Servicios" },
  { key: "reporte-z", icon: Calculator, label: "Cierre de Caja (Z)" },
];

const menu =
  userRole === "supervisor"
    ? allMenu.filter((item) =>
        [
          "venta",
          "crear-producto",
          "crear-categoria",
          "crear-tax",
          "admin-productos",
          "cupones-descuento",
          "reporte-z",
        ].includes(item.key)
      )
    : allMenu;

const seccion = ref("venta");

const componenteActual = computed(() => {
  const componentes = {
    venta: POSVenta,
    "crear-producto": CrearProducto,
    "crear-categoria": CrearCategoria,
    "crear-tax": CrearTax,
    "cupones-descuento": CuponesDescuento,
    "reporte-ventas": ReporteVentas,
    "reporte-inventario": ReporteInventario,
    "top-productos": ReporteTopProductos,
    "top-servicios": ReporteTopServicios,
    "reporte-z": ReporteZ,
    "admin-productos": AdminProductos,
  };

  // fallback visual seguro si el key no existe
  return (
    componentes[seccion.value] || {
      template:
        '<div class="text-center text-gray-500 py-20">Vista no encontrada</div>',
    }
  );
});
</script>
